import React, { useState } from 'react'
import { DataTable, Button, PageHeader } from '@/shared/ui'
import type { AdminRole, Column, UserListItemResponse } from '@/shared/api'
import { USER_ROLE } from '@/shared/api'
import { useUsers, usePagination, useAdminRoles, useAssignAdminRole, useCreateAdminUser } from '@/shared/hooks'
import { CreateEmployeeModal } from './CreateEmployeeModal'

export const EmployeesPage: React.FC = () => {
  const { page, limit, setPage } = usePagination({ initialLimit: 50 })
  const { data, isLoading } = useUsers({ page, limit, role: USER_ROLE.ADMIN })
  const { data: roles } = useAdminRoles()
  const assign = useAssignAdminRole()
  const create = useCreateAdminUser()
  const [draft, setDraft] = useState<Record<number, number | null>>({})
  const [createOpen, setCreateOpen] = useState(false)

  const valueFor = (user: UserListItemResponse): number | null =>
    user.user_id in draft ? (draft[user.user_id] ?? null) : (user.admin_role_id ?? null)

  const columns: Column<UserListItemResponse>[] = [
    { key: 'full_name', title: 'Сотрудник' },
    { key: 'email', title: 'Email' },
    {
      key: 'actions',
      title: 'Роль',
      render: (_, user) => (
        <div className="flex items-center gap-2">
          <select
            className="admin-input"
            value={valueFor(user) ?? ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                [user.user_id]: e.target.value ? Number(e.target.value) : null,
              })
            }
          >
            <option value="">— не назначена —</option>
            {(roles?.items || []).map((role: AdminRole) => (
              <option key={role.admin_role_id} value={role.admin_role_id}>
                {role.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            isLoading={assign.isPending}
            onClick={() => assign.mutate({ userId: user.user_id, adminRoleId: valueFor(user) })}
          >
            Сохранить
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        title="Сотрудники"
        description="Учётные записи администраторов и назначение ролей"
      >
        <Button onClick={() => setCreateOpen(true)}>Создать сотрудника</Button>
      </PageHeader>
      <div className="p-6">
        <DataTable
          data={data?.items || []}
          columns={columns}
          loading={isLoading}
          emptyMessage="Администраторов не найдено"
          pagination={
            data
              ? {
                  page: data.page,
                  limit: data.limit,
                  total: data.total,
                  pages: data.pages,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>
      <CreateEmployeeModal
        isOpen={createOpen}
        loading={create.isPending}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (data) => {
          try {
            await create.mutateAsync(data)
            setCreateOpen(false)
          } catch {
            // ошибка уже показана тостом в useCreateAdminUser
          }
        }}
      />
    </div>
  )
}
